/**
 * REST client for the session-restore API (`/agent-sessions/*` on the async
 * HTTP API). Backs the console's Sessions panel: list a principal's prior
 * conversations, restore one's full transcript, and delete one.
 *
 * Auth is the Cognito **access token** as a bearer — the async API's JWT
 * authorizer matches it on `client_id` (not SigV4; `signedFetch` is for the
 * IAM-signed routes). Error handling mirrors `agentMemoryApi.ts`: throw a
 * user-friendly message on a non-OK response, log the status detail to the
 * console for debugging.
 *
 * `agentId` is the agent's slug — the same value the turn stream sends and the
 * runtime keys the stored transcript under (`tmp/sessions/<agentId>/...`).
 */

import { requestJson, requestNoContent } from './apiClient.ts';
import { asyncApiConfig } from '../config/async-api-config.ts';

/** One row in the sessions list. Mirrors the Lambda's `SessionSummary`. */
export interface SessionSummary {
  readonly sessionId: string;
  readonly title: string;
  readonly startedAt: string;
  readonly lastTurnAt: string;
  readonly turnCount: number;
}

/** One role-tagged turn in a restored transcript. */
/** Records the agent recalled into context this turn (assistant turns), persisted
 *  losslessly by the runtime so a restored session can relight the graph. */
export interface TranscriptRecall {
  readonly moduleId: string;
  readonly records: readonly {
    readonly id: string;
    readonly kind: 'turn' | 'fact';
    readonly text: string;
    readonly ts: string;
  }[];
}

/** An image-generation job enqueued on an assistant turn (written by the
 *  runtime since 2026-07; older transcript lines omit the field). The console
 *  re-creates the in-chat image card from it on restore. */
export interface TranscriptImageJob {
  readonly jobId: string;
  readonly prompt: string;
}

/**
 * One tool call from a persisted turn, in the lossless-enough form the console
 * rebuilds its tool card and Tool Use row from when a dropped stream forces a
 * transcript recovery. Older transcripts simply omit it.
 *
 * `result` is bounded (see `resultBytes`): full web-search and page-read
 * payloads would bloat every session restore, which downloads the whole
 * transcript. When it was cut, the string itself SAYS so — a reader that gets
 * a silent prefix would treat a fragment as the whole result.
 */
export interface TranscriptToolCall {
  /** Identity across the live stream and the transcript, so recovery can tell
   *  a call it already rendered from one whose frame never arrived. */
  readonly toolUseId: string;
  readonly name: string;
  readonly args: Record<string, unknown>;
  /** No `ok`: a call only reaches the transcript once it RETURNED, so the flag
   *  could only ever be `true` — the live path sets it the same way in
   *  `endTool`. A tool's own failure is encoded in `result`, not here. */
  readonly result: string;
  /** Byte length of the UNtruncated result — present only when `result` was
   *  cut, so `resultBytes !== undefined` is exactly "this is a fragment". */
  readonly resultBytes?: number;
}

export interface SessionTranscriptTurn {
  readonly role: 'user' | 'assistant';
  readonly text: string;
  readonly ts: string;
  readonly toolSummary?: string;
  readonly recalled?: readonly TranscriptRecall[];
  readonly imageJobs?: readonly TranscriptImageJob[];
  /** Structured tool calls this turn made. Absent on transcripts written
   *  before the runtime persisted them.
   *
   *  NOT `tools`: the runtime's `sessions.ts` serves a `tools` field of a
   *  different shape (memory-record digests), and two incompatible shapes
   *  under one name on this type would be read wrong by whichever consumer
   *  guessed second. */
  readonly toolCalls?: readonly TranscriptToolCall[];
}

export interface SessionTranscript {
  readonly agentId: string;
  readonly sessionId: string;
  readonly turns: readonly SessionTranscriptTurn[];
}

function sessionsBase(agentId: string): string {
  return `${asyncApiConfig.baseUrl}/agent-sessions/${encodeURIComponent(agentId)}`;
}

/** List the signed-in user's sessions for an agent, most-recent first. */
export async function listSessions(agentId: string): Promise<readonly SessionSummary[]> {
  const body = await requestJson<{ sessions?: readonly SessionSummary[] }>(sessionsBase(agentId), {
    logTag: 'sessionApi',
    friendlyError: 'Could not load your sessions.',
  });
  return body.sessions ?? [];
}

/** Fetch one session's full transcript for restore. */
export async function getSession(
  agentId: string,
  sessionId: string,
): Promise<SessionTranscript> {
  return requestJson<SessionTranscript>(
    `${sessionsBase(agentId)}/${encodeURIComponent(sessionId)}`,
    { logTag: 'sessionApi', friendlyError: 'Could not open that session.' },
  );
}

/** Delete one session (transcript + index). Idempotent server-side. */
export async function deleteSession(agentId: string, sessionId: string): Promise<void> {
  await requestNoContent(`${sessionsBase(agentId)}/${encodeURIComponent(sessionId)}`, {
    method: 'DELETE',
    logTag: 'sessionApi',
    friendlyError: 'Could not delete that session.',
  });
}
